import { z } from 'zod'

export const managementOutlookInputSchema = z.object({
  // Current month plus the next N-1. Four covers a quarter's decisions plus the
  // month being closed.
  months: z.number().int().min(1).max(18).default(4),
  // 'YYYY-MM' to pan the window; defaults to the current month. Past months are
  // allowed — the timeline reads better with the run-up visible.
  startMonth: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
})

export const rateSourceSchema = z.enum([
  'sameMonthLastYear',
  'trailing',
  'companyAverage',
  'override',
])

// The four shares of a sale cohort, summing to 1. Carried per team as well as
// company-wide, plus whether the team was thick enough to use its own.
export const salesLagShareSchema = z.object({
  sameMonth: z.number(),
  prevMonth: z.number(),
  twoMonthsBack: z.number(),
  older: z.number(),
  revenueSampled: z.number(),
  // False when the team's own cohort was too thin and the company blend stood in.
  ownSample: z.boolean(),
})

// Open work, kept apart because a visit under way is not a booking: see
// docs/revenue-terms.md. Carried by every unit the report stacks — a team, the
// subcontracting row and the company total. Both halves are already discounted
// by the measured realization rate, so realized + these two + headroom is the
// unit's capacity.
const openWorkShape = {
  inProgress: z.number(),
  scheduled: z.number(),
  // Open work anchored on a day that has already passed. A finding, never a
  // tier: the anchor is the day it can be delivered on, so once that day is
  // gone the work is late rather than forthcoming. The rule is a day rule, so
  // the running month carries one of these too — not only closed months.
  overdueOpenWork: z.number(),
}

export const teamOutlookSchema = z.object({
  teamId: z.number().nullable(),
  teamName: z.string(),
  availableDays: z.number(),
  absenceDays: z.number(),
  headcount: z.number(),
  // Field heads, not heads: the roster's field-work shares summed. A team of six
  // with two part-time supervisors is 4,7 htkk, and the capacity follows the htkk.
  fieldFte: z.number(),
  // Day occupancy: worked days over available days, what the capacity multiplies.
  utilization: z.number(),
  // Slot hours over available hours, shown beside it and never multiplied:
  // driving and hours-only work sit outside any slot.
  hourUtilization: z.number(),
  // The team's own best month in the trailing window — what a utilisation
  // lever proposes getting back to, rather than a round number.
  achievableUtilization: z.number(),
  // The rate the capacity was built on — the override where one is set.
  eurPerDay: z.number(),
  // What the measurement said, kept beside the rate in use so the editor can
  // offer it as the value an empty override cell would take.
  measuredEurPerDay: z.number(),
  // Whether the rate came from the same month a year ago (seasonality
  // modelled), the trailing window (not), the company average (a team with no
  // measurement of its own) or a hand-entered override. The UI has to show
  // this: an autumn figure built on summer rates is wrong in a way no number
  // reveals.
  rateSource: rateSourceSchema,
  capacity: z.number(),
  // Capacity with no hand-entry at all — neither the rate override nor the
  // planned headcount change — so the report can show what was measured beside
  // what was judged.
  computedCapacity: z.number(),
  // The same certainty tiers the company figures carry, for this team alone.
  // Revenue divides across a visit's attendee slots the way capacity does, so
  // the teams add back to the company total rather than double-counting a job
  // two units worked.
  realized: z.number(),
  ...openWorkShape,
  headroom: z.number(),
  eurPerDayOverride: z.number().nullable(),
  // Planned headcount change for this team and month, in person-months: 1.0 is
  // one person for the whole month, 0.5 someone starting mid-month. It moves
  // available days rather than capacity, so it composes with utilisation and
  // the rate instead of replacing their product.
  fteDelta: z.number().nullable(),
  overrideNote: z.string().nullable(),
  // The unit's own targets, null until someone sets a row for this team and
  // month. The company row is not split across teams to fill these in: a target
  // nobody set is unknown, not zero.
  revenueTarget: z.number().nullable(),
  storedSalesTarget: z.number().nullable(),
  // Derived from this team's own lag, which differs enough between units that
  // the company blend cannot size it — roofing converts .09 of a month's selling
  // in-month against sweeping's .54.
  derivedSalesTarget: z.number().nullable(),
  // The unit's own lag, so the UI can show which coefficient sized its target
  // and whether it fell back to the company blend on a thin sample.
  lag: salesLagShareSchema,
  shortfall: z.number(),
})

export const subcontractorOutlookSchema = z.object({
  openedDays: z.number(),
  lastYearDays: z.number(),
  eurPerDay: z.number(),
  capacity: z.number(),
  // Subcontractors belong to a team like anyone else, and the row is one line
  // for all of them. Without this split it reads as a company-wide pool while
  // it is in practice one unit's overflow capacity. Priced on the shared rate,
  // so the parts add back to `capacity`. Largest first.
  byTeam: z.array(
    z.object({
      teamName: z.string(),
      openedDays: z.number(),
      capacity: z.number(),
    }),
  ),
  // Subcontracted work carries its own tiers rather than the teams it rode
  // with: the capacity for those days is here, so the revenue is too.
  realized: z.number(),
  ...openWorkShape,
  headroom: z.number(),
})

export const outlookLeverSchema = z.object({
  key: z.string(),
  label: z.string(),
  detail: z.string(),
  amount: z.number(),
  fteEquivalent: z.number().nullable().optional(),
})

export const monthPhaseSchema = z.enum(['past', 'current', 'future'])

export const monthOutlookSchema = z.object({
  month: z.string(),
  // Whether the month is closed, still running, or ahead. Every actual-vs-target
  // comparison has to carry this: a running month holds a partial actual and a
  // future one holds nothing to compare at all.
  phase: monthPhaseSchema,
  revenueTarget: z.number().nullable(),
  storedSalesTarget: z.number().nullable(),
  derivedSalesTarget: z.number().nullable(),
  // Where the derived figure comes from: this month's target and the two after
  // it, each times the share of its revenue that gets sold this month. A null
  // amount is a month nobody has set a target for, which makes the figure short
  // rather than low.
  salesTargetParts: z.array(
    z.object({
      month: z.string(),
      share: z.number(),
      amount: z.number().nullable(),
    }),
  ),
  // The certainty tiers the report stacks, in order of how sure they are.
  realized: z.number(),
  ...openWorkShape,
  headroom: z.number(),
  // Revenue booked into the month it was sold in — the actual behind the sales
  // target, on the same footing as the target itself.
  actualSales: z.number(),
  capacity: z.object({
    // The two ends of the laskennallinen range, named by the rate that priced
    // them rather than by which is larger: `base` uses the seasonal rate where
    // that is usable, `conservative` always the trailing window. A weak season a
    // year ago puts `conservative` on top, so read them as a sorted pair. Vaje
    // and headroom are measured against the higher of the two.
    base: z.number(),
    conservative: z.number(),
    // Where the month ends if the remaining days' capacity sells out: completed
    // so far plus the capacity of the days ahead. A different base from the two
    // above, which price the elapsed days at the model rate — so it can exceed
    // them, and does whenever the month has run ahead of plan. Only the running
    // month has one; a closed month has no days left and a future month's is
    // its base. The same figure the KPI card shows, so the two reconcile.
    achievable: z.number().nullable(),
  }),
  teams: z.array(teamOutlookSchema),
  subcontractor: subcontractorOutlookSchema,
  shortfall: z.number(),
  levers: z.array(outlookLeverSchema),
})

export const salesLagSchema = z.object({
  sameMonth: z.number(),
  prevMonth: z.number(),
  twoMonthsBack: z.number(),
  older: z.number(),
  sampleMonths: z.array(z.string()),
  revenueSampled: z.number(),
})

export const managementOutlookResponseSchema = z.object({
  months: z.array(monthOutlookSchema),
  lag: salesLagSchema,
  realizationRate: z.number().nullable(),
  trailingMonths: z.array(z.string()),
  generatedAt: z.string(),
})

export type ManagementOutlookInput = z.infer<
  typeof managementOutlookInputSchema
>
export type ManagementOutlookResponse = z.infer<
  typeof managementOutlookResponseSchema
>
export type MonthOutlook = z.infer<typeof monthOutlookSchema>
export type TeamOutlook = z.infer<typeof teamOutlookSchema>
export type OutlookLever = z.infer<typeof outlookLeverSchema>
export type SubcontractorOutlook = z.infer<typeof subcontractorOutlookSchema>
export type MonthPhase = z.infer<typeof monthPhaseSchema>
export type RateSource = z.infer<typeof rateSourceSchema>
